/**
 * Thin client to the auth-proxy worker. The browser never talks to
 * zm-*.zeemart.co directly — it asks /api/zeemart/<service>/<path...> and
 * the worker (which holds the httpOnly mudra cookie) forwards.
 *
 * Every call in this file is **observed**, not guessed — the URL shape,
 * required headers, query params, and method come from
 * docs/observed/screen-flow.md captured against buyer.zeemart.co
 * 2026-05-23. Do not invent endpoints from the JS bundle here; add a new
 * tour step, re-run observe-original.sh, and add the entry.
 */

export type Outlet = {
  outletId: string;
  outletName?: string;
  name?: string;
  timeZone?: string;
  status?: string;
  logoURL?: string;
  company?: { companyId?: string; companyName?: string };
  hqOutlet?: boolean;
  outletType?: string;
};

export type Supplier = {
  supplierId: string;
  supplierName: string;
  deliveryDays?: string;
  minOrder?: string;
};

export type LoginResp = {
  ok: boolean;
  outlets?: Outlet[];
  user?: { firstName?: string; lastName?: string; userId?: string };
  market?: string;
  error?: string;
};

// --- auth -----------------------------------------------------------------

export async function apiLogin(zeemartId: string, password: string): Promise<LoginResp> {
  const r = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ZeemartId: zeemartId, password }),
  });
  return r.json();
}

export async function apiLogout() {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
}

export async function apiSession(): Promise<LoginResp & { userId?: string }> {
  const r = await fetch("/api/auth/session", { credentials: "include" });
  return r.json();
}

// --- generic proxy --------------------------------------------------------

export async function apiZeemart(
  service: string,
  path: string,
  init?: RequestInit & { headers?: Record<string, string> },
) {
  const url = `/api/zeemart/${encodeURIComponent(service)}${path.startsWith("/") ? path : "/" + path}`;
  const r = await fetch(url, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`zeemart ${service}${path}: ${r.status} ${t.slice(0, 300)}`);
  }
  return r.json();
}

// --- outlets / user -------------------------------------------------------

/**
 * The detail-fielded outlets list. Observed shape:
 *   GET /account/outlets?sortBy=name&sortOrder=ASC
 *       &includeFields=outletId,name,company,status,subscriptionSettings,outletType,hqOutlet
 *       &companyId=CAGRK,CAGJM,CAGBY,...
 *
 * The login response gives a basic outlet list; this gives the detail
 * shape the dashboard / sidebar needs.
 */
export async function apiOutlets(companyIds: string[]): Promise<{ data?: Outlet[] } | Outlet[]> {
  const qs = new URLSearchParams({
    sortBy: "name",
    sortOrder: "ASC",
    includeFields: "outletId,name,company,status,subscriptionSettings,outletType,hqOutlet",
    companyId: companyIds.join(","),
  });
  return apiZeemart("accountmanagementserv", `/account/outlets?${qs}`);
}

export async function apiUser(userId: string): Promise<unknown> {
  return apiZeemart("accountmanagementserv", `/account/user?userId=${encodeURIComponent(userId)}`);
}

// --- suppliers (CORRECTED: linkedSuppliersByOutlet, comma-sep) ------------

/**
 * The real suppliers call from buyer.zeemart.co. The endpoint takes a
 * COMMA-SEPARATED outletId list — my prior version used singular
 * /linkedSuppliers?outletId=ONE and silently returned empty data[].
 */
export async function apiLinkedSuppliersByOutlet(
  outletIds: string[],
): Promise<{ data?: Supplier[] } | Supplier[]> {
  const ids = outletIds.join(",");
  return apiZeemart(
    "inventorymanagement",
    `/services/inventory/deliveryPreference/linkedSuppliersByOutlet?outletId=${encodeURIComponent(ids)}`,
    { headers: { outletId: ids } },
  );
}

export async function apiDeliverySettings(): Promise<unknown> {
  const qs = new URLSearchParams({
    sortBy: "supplier.supplierName",
    sortOrder: "asc",
    includeFields: "[supplier.supplierName,supplier.supplierId]",
  });
  return apiZeemart("inventorymanagement", `/services/inventory/deliveryPreference/delivery/settings?${qs}`);
}

// --- reports family (the dashboard, observed) -----------------------------
//
// CRITICAL header contract (from docs/observed/screen-flow.md):
//   outletId: <comma-separated outletIds>     # ALL outlets the user can act for
//   authType: Zeemart
//   mudra: <session>
// Without the outletId header Zeemart returns:
//   {"message":"Missing mandatory Header Parameters: [supplierId,companyId,outletId]"}

function outletIdHeader(outletIds: string[]): Record<string, string> {
  return { outletId: outletIds.join(",") };
}

/** Daily/weekly buckets for spending charts. */
type Bucket = { startDate: number; endDate: number; groupBy?: "weekly" | "daily" };

export async function apiReportOrderSummary(outletIds: string[]): Promise<unknown> {
  return apiZeemart("reportmanagementserv", `/services/reports/order/summary`, {
    headers: outletIdHeader(outletIds),
  });
}

/** Spending over a time range. groupBy is the bucketing axis. */
export async function apiReportSpendingsRange(outletIds: string[], b: Bucket): Promise<unknown> {
  const qs = new URLSearchParams({
    startDate: String(b.startDate),
    endDate: String(b.endDate),
    groupBy: b.groupBy ?? "weekly",
  });
  return apiZeemart("reportmanagementserv", `/services/reports/spendings/range/v1?${qs}`, {
    headers: outletIdHeader(outletIds),
  });
}

/** Spending sliced by an arbitrary key (supplier, category, …). */
export async function apiReportSpendingsBy(
  outletIds: string[],
  startDate: number,
  endDate: number,
  refStartDate: number,
  refEndDate: number,
  groupBy: "supplier" | "category" | "sku",
): Promise<unknown> {
  const qs = new URLSearchParams({
    startDate: String(startDate),
    endDate: String(endDate),
    refStartDate: String(refStartDate),
    refEndDate: String(refEndDate),
  });
  const headers = outletIdHeader(outletIds);
  if (groupBy === "supplier") {
    qs.set("groupBy", "supplier");
    return apiZeemart("reportmanagementserv", `/services/reports/spendings?${qs}`, { headers });
  }
  // /spendings/sku and /spendings/category don't take groupBy in query
  return apiZeemart("reportmanagementserv", `/services/reports/spendings/${groupBy}?${qs}`, { headers });
}

export async function apiReportInsights(
  outletIds: string[],
  range: "PAST_7_DAYS" | "PAST_30_DAYS" = "PAST_7_DAYS",
): Promise<unknown> {
  return apiZeemart("reportmanagementserv", `/services/reports/insights?range=${range}`, {
    headers: outletIdHeader(outletIds),
  });
}

// --- notifications --------------------------------------------------------

export async function apiNotificationCount(): Promise<unknown> {
  return apiZeemart("notificationserv", `/services/notification/announcements/count`);
}

export async function apiNotifications(): Promise<unknown> {
  return apiZeemart("notificationserv", `/services/notification`);
}

// --- common reference data (the baseline every screen warms) -------------

export async function apiPaymentTerms(): Promise<unknown> {
  return apiZeemart("commonservices", `/services/common/invoices/paymentTerms`);
}
